<template>
  <div
    :class="
      classes(
        n(),
        n(`--${state.type}`),
        n(`--${state.background}`),
        n(`--${state.size}`),
      )
    "
    :style="tabsStyle"
  >
    <!-- 标签栏区域 -->
    <div
      :class="
        classes(
          n('nav-wrapper'),
          [state.showGradient && hasOverflow && showLeftGradient, n('nav-wrapper--gradient-left')],
          [state.showGradient && hasOverflow && showRightGradient, n('nav-wrapper--gradient-right')],
        )
      "
    >
      <!-- 左侧插槽 -->
      <div
        v-if="$slots['nav-left']"
        :class="n('nav-left')"
      >
        <slot
          name="nav-left"
          :active-index="activeIndex"
        />
      </div>

      <!-- 标签栏 - liteapp 环境使用 scroll-view -->
      <scroll-view
        v-if="isLiteapp"
        ref="tabsNavRef"
        :class="n('nav')"
        scroll-x
        :scroll-left="scrollLeft"
        :scroll-with-animation="scrollWithAnimation"
        :show-scrollbar="false"
        @scroll="handleScrollViewScroll"
        @scrollend="handleScrollViewScrollEnd"
      >
        <div :class="n('nav-track')">
          <slot />
          <!-- 底部指示器 (仅 normal 类型) -->
          <div
            v-if="state.type === 'normal'"
            ref="indicatorRef"
            :class="n('indicator')"
            :style="indicatorStyle"
          />
        </div>
      </scroll-view>

      <!-- 标签栏 - H5 环境使用原生 div -->
      <div
        v-else
        ref="tabsNavRef"
        :class="n('nav')"
        @scroll="handleScroll"
      >
        <div :class="n('nav-track')">
          <slot />
          <!-- 底部指示器 (仅 normal 类型) -->
          <div
            v-if="state.type === 'normal'"
            ref="indicatorRef"
            :class="n('indicator')"
            :style="indicatorStyle"
          />
        </div>
      </div>

      <!-- 右侧插槽 -->
      <div
        v-if="$slots['nav-right']"
        :class="n('nav-right')"
      >
        <slot
          name="nav-right"
          :active-index="activeIndex"
        />
      </div>
    </div>

    <!-- 内容区域 -->
    <div
      v-if="state.swipeable"
      :class="n('content')"
    >
      <div
        :class="n('content-track')"
        :style="{
          transform: `translateX(-${activeIndexNum * 100}%)`,
          transition: state.animated ? `transform ${state.duration}ms ease` : 'none',
        }"
      >
        <slot name="content" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onMounted, provide, ref, toRefs, watch } from 'virtual-vue';
import { tabsProps } from './props';
import { tabsEmits } from './emits';
import { TABS_INJECT_KEY } from './provide';
import { useVModel } from '../hooks/use-v-model';
import { createNamespace } from '../utils/components';
import { platform } from '@tencent/wphk-platform-adapter';
import type { TabsIndex } from './tabs';

const { name, n, classes } = createNamespace('tabs');

export default defineComponent({
  name,
  props: tabsProps,
  emits: tabsEmits,
  setup(props, { emit }) {
    // ==================== 响应式状态定义 ====================
    const activeIndex = useVModel(props);
    const tabsNavRef = ref<HTMLElement>();
    const indicatorRef = ref<HTMLElement>();
    const hasOverflow = ref(false);
    const showLeftGradient = ref(false);
    const showRightGradient = ref(false);

    // ==================== Liteapp 环境适配 ====================
    // Liteapp 和小程序环境不支持 overflow: scroll，需要使用 scroll-view 组件
    const isLiteapp = platform.isLiteapp();
    /** scroll-view 组件的横向滚动位置（仅 liteapp 环境使用） */
    const scrollLeft = ref(0);
    /** scroll-view 是否启用滚动动画（仅 liteapp 环境使用） */
    const scrollWithAnimation = ref(false);
    /**
     * 记录程序化滚动的目标位置，用于区分程序化滚动和用户手动滚动
     * - null: 表示当前没有程序化滚动，或者程序化滚动已结束
     * - number: 表示正在进行程序化滚动，值为目标滚动位置
     *
     * 这个标记的作用：
     * 1. 防止程序化滚动时触发不必要的 scroll 事件
     * 2. 避免响应式更新导致的滚动循环问题
     */
    const lastProgrammaticScrollLeft = ref<number | null>(null);

    // ==================== 计算属性 ====================
    /** 组件状态的计算属性集合 */
    const state = computed(() => ({
      type: props.type,              // 标签类型：normal | button | card
      background: props.background,  // 背景色：white | gray
      size: props.size,              // 尺寸：small | medium | large
      showGradient: props.showGradient,  // 是否显示渐变遮罩
      swipeable: props.swipeable,    // 是否支持滑动切换
      animated: props.animated,      // 是否启用切换动画
      duration: props.duration,      // 动画持续时间
    }));

    /** 将 activeIndex 转换为数字类型，用于计算内容区域的偏移 */
    const activeIndexNum = computed(() => {
      const index = Number(activeIndex.value);
      return isNaN(index) ? 0 : index;
    });

    /**
     * 标签栏的自定义样式
     * 通过 CSS 变量的方式注入激活和非激活状态的颜色
     */
    const tabsStyle = computed(() => {
      const style: Record<string, string> = {};
      if (props.activeColor) {
        style['--tabs-active-color'] = props.activeColor;
      }
      if (props.inactiveColor) {
        style['--tabs-inactive-color'] = props.inactiveColor;
      }
      return style;
    });

    /** 底部指示器的动态样式（位置、宽度和颜色） */
    const indicatorStyle = ref<Record<string, string>>({});

    // ==================== 核心方法 ====================

    /**
     * 更新激活的标签
     * @param index - 标签索引
     * @param title - 标签标题（可选）
     *
     * 执行流程：
     * 1. 检查是否需要更新（避免重复更新）
     * 2. 更新 activeIndex 状态
     * 3. 触发 change 事件
     * 4. 在下一帧更新指示器位置和滚动位置
     */
    const updateActive = (index: TabsIndex, title?: string) => {
      // 如果点击的是当前激活的标签，不做任何处理
      if (activeIndex.value === index) {
        return;
      }

      // 更新激活索引
      activeIndex.value = index;

      // 触发 change 事件，通知父组件
      emit('change', index, title || '');

      // 在 DOM 更新后执行后续操作
      nextTick(() => {
        updateIndicator();      // 更新底部指示器位置
        scrollToCenter(index);  // 滚动到合适位置
      });
    };

    /**
     * 更新底部指示器的位置和宽度（仅 normal 类型使用）
     *
     * 计算逻辑：
     * 1. 找到当前激活的标签元素
     * 2. 获取标签的位置和尺寸信息
     * 3. 计算指示器的宽度（基于内容宽度，限制在 48-112px 之间）
     * 4. 计算指示器的水平位置（居中对齐）
     * 5. 通过 transform 和 width 样式更新指示器
     */
    const updateIndicator = () => {
      // 只有 normal 类型才有底部指示器
      if (state.value.type !== 'normal' || !tabsNavRef.value) {
        return;
      }

      // 获取导航轨道容器
      // 兼容 liteapp 环境，使用 querySelector 或 children[0]
      const navTrack = tabsNavRef.value.querySelector(`.${n('nav-track')}`) || (tabsNavRef.value as HTMLElement).children[0] as HTMLElement;
      if (!navTrack) {
        return;
      }

      // 获取所有标签项（从 nav-track 中查找）
      const items = Array.from(navTrack.querySelectorAll(`.${n('item')}`));

      // 找到当前激活的标签项
      const activeItem = items.find((item: any) => item.dataset.index === String(activeIndex.value)) as HTMLElement;

      if (activeItem) {
        // 获取标签的 padding 值
        // 兼容 liteapp 环境：getComputedStyle 返回 undefined，不会报错
        let paddingLeft = 0;
        let paddingRight = 0;

        if (isLiteapp) {
          // ===== Liteapp 环境 =====
          // 在 liteapp 中，getComputedStyle 返回 undefined，使用 offsetWidth 和 clientWidth 的差值计算总 padding
          // 注意：这只能得到左右 padding 的总和，无法分别获取左右 padding
          const { offsetWidth } = activeItem;
          const { clientWidth } = activeItem;
          const totalHorizontalPadding = offsetWidth - clientWidth;

          // 假设左右 padding 相等（通常情况）
          paddingLeft = totalHorizontalPadding / 2;
          paddingRight = totalHorizontalPadding / 2;
        } else {
          // ===== H5 环境 =====
          // 在 H5 中，使用 getComputedStyle 获取精确的 padding 值
          const computedStyle = document.defaultView?.getComputedStyle?.(activeItem);
          if (computedStyle) {
            paddingLeft = parseFloat(computedStyle.paddingLeft || '0') || 0;
            paddingRight = parseFloat(computedStyle.paddingRight || '0') || 0;
          } else {
            // 如果 getComputedStyle 不可用，尝试从元素的 style 属性获取
            const { style } = (activeItem as any);
            if (style) {
              paddingLeft = parseFloat(style.paddingLeft || '0') || 0;
              paddingRight = parseFloat(style.paddingRight || '0') || 0;
            }
          }
        }
        const totalPadding = paddingLeft + paddingRight;

        // 获取文本标题元素（item-title），指示器宽度应该基于文本宽度
        const titleElement = activeItem.querySelector(`.${n('item-title')}`) as HTMLElement;
        if (!titleElement) {
          return;
        }

        if (isLiteapp) {
          // ===== Liteapp 环境 =====
          // 在 liteapp 中，使用 offsetLeft 和 offsetWidth 计算位置
          // 因为指示器和标签都在 nav-track 内部，所以相对于 nav-track 计算即可
          const itemLeft = activeItem.offsetLeft;

          // 计算指示器宽度
          // 如果传入了 indicatorWidth，优先使用传入的值，否则使用文本宽度
          const textWidth = titleElement.offsetWidth;
          const baseWidth = props.indicatorWidth !== undefined ? props.indicatorWidth : textWidth;

          // 指示器宽度限制在 48-112px 之间
          // 默认与文本宽度一致，最小 48px，最大 112px
          const width = Math.min(Math.max(baseWidth, 48), 112);

          // 计算指示器的左侧位置（相对于 nav-track）
          // 指示器应该居中对齐在 item 内部
          // 位置 = item左侧 + (item宽度 - 指示器宽度) / 2
          const itemWidth = activeItem.offsetWidth;
          const left = itemLeft + (itemWidth - width) / 2;

          // 构建指示器样式
          const style: Record<string, string> = {
            transform: `translateX(${left}px)`,
            width: `${width}px`,
          };

          // 如果传入了自定义颜色，则设置背景色
          if (props.indicatorColor) {
            style.backgroundColor = props.indicatorColor;
          }

          indicatorStyle.value = style;
        } else {
          // ===== H5 环境 =====
          // 在 H5 中，使用 getBoundingClientRect 计算位置
          const navRect = tabsNavRef.value.getBoundingClientRect();
          const itemRect = activeItem.getBoundingClientRect();
          const titleRect = titleElement.getBoundingClientRect();

          // 计算指示器宽度
          // 如果传入了 indicatorWidth，优先使用传入的值，否则使用文本宽度
          const textWidth = titleRect.width;
          const baseWidth = props.indicatorWidth !== undefined ? props.indicatorWidth : textWidth;

          // 指示器宽度限制在 48-112px 之间
          // 默认与文本宽度一致，最小 48px，最大 112px
          const width = Math.min(Math.max(baseWidth, 48), 112);

          // 计算指示器的左侧位置（相对于导航栏容器）
          // 指示器应该居中对齐在 item 内部
          const currentScrollLeft = (tabsNavRef.value as HTMLElement).scrollLeft || 0;
          const itemCenter = itemRect.left - navRect.left + currentScrollLeft + itemRect.width / 2;
          const left = itemCenter - width / 2;

          // 构建指示器样式
          const style: Record<string, string> = {
            transform: `translateX(${left}px)`,
            width: `${width}px`,
          };

          // 如果传入了自定义颜色，则设置背景色
          if (props.indicatorColor) {
            style.backgroundColor = props.indicatorColor;
          }

          // 更新指示器样式
          indicatorStyle.value = style;
        }
      }
    };

    /**
     * 自动滚动到指定标签，使其居中显示
     * @param index - 目标标签索引
     *
     * 滚动策略：
     * - 前 4 个标签不触发滚动（通常能完整显示）
     * - 其他标签滚动到容器中心位置
     *
     * 环境适配：
     * - H5: 使用原生 scrollTo API
     * - Liteapp: 使用 scroll-view 组件的 scroll-left 属性
     */
    const scrollToCenter = (index: TabsIndex) => {
      // 将索引转换为数字
      const indexNum = typeof index === 'number' ? index : parseInt(String(index), 10);

      // 前 4 个标签不滚动（优化体验，避免不必要的滚动）
      if (indexNum < 4) {
        return;
      }

      const container = tabsNavRef.value;
      if (!container) {
        return;
      }

      // 获取导航轨道容器
      // 兼容 liteapp 环境，使用 querySelector 或 children[0]
      const navTrack = container.querySelector(`.${n('nav-track')}`) || container.children[0];
      if (!navTrack) {
        return;
      }

      // 查找目标标签元素
      // 兼容 liteapp 环境，使用遍历方式而非 querySelector
      // 因为 liteapp 对属性选择器的支持不完整
      let targetTab: HTMLElement | null = null;
      for (let i = 0; i < navTrack.children.length; i++) {
        const child = navTrack.children[i] as HTMLElement;
        const dataIndex = child.getAttribute('data-index');
        if (dataIndex === String(index)) {
          targetTab = child;
          break;
        }
      }

      if (!targetTab) {
        return;
      }

      // 获取容器和目标标签的尺寸信息
      const containerWidth = container.offsetWidth;  // 容器可视宽度
      const targetLeft = targetTab.offsetLeft;       // 目标标签左侧位置
      const targetWidth = targetTab.offsetWidth;     // 目标标签宽度

      // 计算滚动位置，使目标标签居中
      // 公式：目标左侧位置 - (容器宽度 / 2) + (目标宽度 / 2)
      const targetScrollLeft = targetLeft - (containerWidth / 2) + (targetWidth / 2);

      // 确保滚动位置不小于 0
      const finalScrollLeft = Math.max(0, targetScrollLeft);

      if (isLiteapp) {
        // ===== Liteapp 环境 =====
        // 使用 scroll-view 组件的响应式属性进行滚动

        // 1. 记录程序化滚动的目标位置（用于后续事件处理中的判断）
        lastProgrammaticScrollLeft.value = finalScrollLeft;

        // 2. 启用滚动动画
        scrollWithAnimation.value = true;

        // 3. 更新滚动位置（触发 scroll-view 的滚动）
        //    注意：这是唯一更新 scrollLeft.value 的地方，避免响应式循环
        scrollLeft.value = finalScrollLeft;
      } else {
        // ===== H5 环境 =====
        // 使用原生 scrollTo API，带平滑动画
        container.scrollTo({
          left: finalScrollLeft,
          behavior: 'smooth',
        });
      }
    };

    /**
     * 检查标签栏是否溢出，并更新渐变遮罩的显示状态
     * @param currentScrollLeft - 当前滚动位置（可选，用于避免重复读取响应式变量）
     *
     * 遮罩显示逻辑：
     * - 左侧遮罩：内容溢出 && 不在最左侧
     * - 右侧遮罩：内容溢出 && 不在最右侧
     *
     * 性能优化：
     * - 传入 currentScrollLeft 参数可避免重复读取响应式变量
     * - 添加 1px 容差避免浮点数精度问题
     */
    const checkOverflow = (currentScrollLeft?: number) => {
      if (!tabsNavRef.value) {
        return;
      }
      const nav = tabsNavRef.value;

      // 检查内容是否溢出（滚动宽度 > 可视宽度）
      hasOverflow.value = nav.scrollWidth > nav.clientWidth;

      // 获取当前滚动位置
      // 优先使用传入的参数（避免读取响应式变量导致循环更新）
      const scrollPosition = currentScrollLeft !== undefined
        ? currentScrollLeft
        : (isLiteapp ? scrollLeft.value : nav.scrollLeft);

      // 判断滚动位置（添加 1px 容差，避免浮点数精度问题）
      const isAtStart = scrollPosition <= 1;  // 是否在最左侧
      const isAtEnd = scrollPosition >= nav.scrollWidth - nav.clientWidth - 1;  // 是否在最右侧

      // 更新遮罩显示状态
      showLeftGradient.value = hasOverflow.value && !isAtStart;   // 左侧遮罩
      showRightGradient.value = hasOverflow.value && !isAtEnd;    // 右侧遮罩
    };

    // ==================== 事件处理 ====================

    /**
     * 处理滚动事件 - H5 环境
     * @param event - 原生滚动事件
     *
     * 处理流程：
     * 1. 获取当前滚动位置
     * 2. 触发 scroll 事件（通知父组件）
     * 3. 更新渐变遮罩状态
     */
    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      const currentScrollLeft = target.scrollLeft;

      // 触发 scroll 事件
      emit('scroll', currentScrollLeft);

      // 更新遮罩显示状态（传入当前滚动位置避免重复读取）
      checkOverflow(currentScrollLeft);
    };

    /**
     * 处理滚动事件 - Liteapp 环境
     * @param event - scroll-view 组件的滚动事件
     *
     * 处理流程：
     * 1. 获取当前滚动位置
     * 2. 更新渐变遮罩状态
     * 3. 仅在用户手动滚动时触发 scroll 事件（程序化滚动不触发）
     *
     * 注意：
     * - 程序化滚动时不触发事件，避免不必要的事件通知
     * - 通过 lastProgrammaticScrollLeft 标记区分滚动来源
     */
    const handleScrollViewScroll = (event: any) => {
      const detail = event.detail || event;
      const currentScrollLeft = detail.scrollLeft || 0;

      // 更新遮罩状态（滚动过程中实时更新）
      checkOverflow(currentScrollLeft);

      // 只有在用户手动滚动时才发送 scroll 事件
      // 程序化滚动（点击标签触发）时不发送，避免不必要的事件通知
      if (lastProgrammaticScrollLeft.value === null) {
        emit('scroll', currentScrollLeft);
      }
    };

    /**
     * 处理滚动结束事件 - Liteapp 环境
     * @param event - scroll-view 组件的 scrollend 事件
     *
     * 处理流程：
     * 1. 关闭滚动动画
     * 2. 更新渐变遮罩状态
     * 3. 仅在用户手动滚动时触发 scroll 事件
     * 4. 清除程序化滚动标记
     *
     * 重要说明：
     * - 不要在这里更新 scrollLeft.value！
     * - scrollLeft.value 只在 scrollToCenter 中更新（单向数据流）
     * - 如果在这里更新，会触发 scroll-view 的响应式更新，导致滚动循环
     */
    const handleScrollViewScrollEnd = (event: any) => {
      const detail = event.detail || event;
      const currentScrollLeft = detail.scrollLeft || 0;

      // 1. 关闭滚动动画
      scrollWithAnimation.value = false;

      // 2. 更新遮罩状态
      checkOverflow(currentScrollLeft);

      // 3. 如果是用户手动滚动结束，发送 scroll 事件
      if (lastProgrammaticScrollLeft.value === null) {
        emit('scroll', currentScrollLeft);
      }

      // 4. 清除程序化滚动标记（标记滚动已完成）
      lastProgrammaticScrollLeft.value = null;

      // ⚠️ 重要：不要在这里更新 scrollLeft.value
      // 原因：scrollLeft.value 已经在 scrollToCenter 中设置过了
      // 如果这里再次更新，会触发 scroll-view 的响应式更新，导致额外的滚动
      // 保持单向数据流：scrollToCenter 是唯一更新 scrollLeft.value 的地方
    };

    // ==================== 依赖注入 ====================

    /**
     * 向子组件（TabItem）提供上下文数据
     * 子组件通过 inject(TABS_INJECT_KEY) 获取这些数据
     */
    const propsRefs = toRefs(props);
    provide(TABS_INJECT_KEY, {
      activeIndex,          // 当前激活的标签索引
      size: propsRefs.size,              // 标签尺寸
      type: propsRefs.type,              // 标签类型
      background: propsRefs.background,  // 背景色
      activeColor: propsRefs.activeColor,      // 激活颜色
      inactiveColor: propsRefs.inactiveColor,  // 非激活颜色
      updateActive,         // 更新激活状态的方法
    });

    // ==================== 生命周期和监听 ====================

    /**
     * 监听 activeIndex 变化
     * 当外部通过 v-model 改变 activeIndex 时，更新指示器位置
     */
    watch(activeIndex, () => {
      nextTick(() => {
        updateIndicator();
      });
    });

    /**
     * 组件挂载后的初始化
     * 1. 更新指示器位置（定位到初始激活的标签）
     * 2. 检查是否溢出（决定是否显示渐变遮罩）
     *
     * 注意：在 liteapp 环境中，需要额外的延迟确保 DOM 完全渲染
     * 特别是在页面刷新后，scroll-view 组件可能需要更多时间初始化
     */
    onMounted(() => {
      nextTick(() => {
        if (isLiteapp) {
          // liteapp 环境中，使用 setTimeout 确保 DOM 完全渲染
          // 特别是在页面刷新后，scroll-view 组件需要时间初始化
          setTimeout(() => {
            updateIndicator();
            checkOverflow();
          }, 0);
        } else {
          updateIndicator();
          checkOverflow();
        }
      });
    });

    // ==================== 返回模板使用的数据和方法 ====================

    return {
      // 工具函数
      n,                    // 生成 BEM 类名
      classes,              // 合并类名
      state,                // 组件状态集合
      activeIndex,          // 当前激活索引
      activeIndexNum,       // 激活索引的数字形式
      tabsStyle,            // 自定义样式
      tabsNavRef,           // 导航栏容器引用
      indicatorRef,         // 指示器引用
      indicatorStyle,       // 指示器样式
      hasOverflow,          // 是否溢出
      showLeftGradient,     // 显示左侧遮罩
      showRightGradient,    // 显示右侧遮罩
      handleScroll,                 // H5 滚动事件
      handleScrollViewScroll,       // Liteapp 滚动事件
      handleScrollViewScrollEnd,    // Liteapp 滚动结束事件
      isLiteapp,            // 是否为 liteapp 环境
      scrollLeft,           // scroll-view 滚动位置
      scrollWithAnimation,  // scroll-view 动画开关
    };
  },
});
</script>

